// src/components/sections/Services.ts - Version avec nouveau système V2
import { services, textContent, animationConfig, ServiceDetailExtended } from './ServicesConfig';
import { colors } from '../../utils/colors';
import { ModalSystemV2 } from '../modals-v2';
import '../../styles/components/services.css';
import '../../styles/components/modal-navigation.css';
import '../../styles/components/modal-button-animations.css';

export default class Services {
  private element: HTMLElement;
  private cleanup: Array<() => void> = [];

  constructor() {
    this.element = document.createElement('section');
    this.element.id = 'services';
    this.element.className = 'py-24 bg-gradient-to-b from-[#0b1220]/70 via-[#0b1220]/80 to-[#0b1220]/90 relative overflow-hidden';
    this.render();
    this.setupEventListeners();
  }

  private render(): void {
    const container = document.createElement('div');
    container.className = 'container relative z-10';

    const backgroundEffect = document.createElement('div');
    backgroundEffect.innerHTML = this.renderBackgroundEffect();
    this.element.appendChild(backgroundEffect);

    const titleSection = document.createElement('div');
    titleSection.innerHTML = this.renderTitleSection();
    container.appendChild(titleSection);

    const servicesGrid = document.createElement('div');
    servicesGrid.className = 'services-grid';
    servicesGrid.innerHTML = this.renderServiceCardSection(services);
    container.appendChild(servicesGrid);

    const ctaSection = document.createElement('div');
    ctaSection.innerHTML = this.renderCTA();
    container.appendChild(ctaSection);

    this.element.appendChild(container);

  }

  private setupEventListeners(): void {
    setTimeout(() => {
      this.setupServiceCards();
      this.setupScrollAnimations();
    }, 300);
  }

  private setupServiceCards(): void {
    const serviceCards = this.element.querySelectorAll('.service-card');

    serviceCards.forEach(card => {
      const detailBtn = card.querySelector('.service-details-btn');
      if (detailBtn) {
        const clone = detailBtn.cloneNode(true);
        detailBtn.parentNode?.replaceChild(clone, detailBtn);
      }
    });

    serviceCards.forEach(card => {
      const serviceId = card.getAttribute('data-service-id');
      const detailBtn = card.querySelector('.service-details-btn');

      if (detailBtn && serviceId) {
        const handleClick = async (e: Event) => {
          e.preventDefault();
          // Utiliser le nouveau système V2
          try {
            await ModalSystemV2.openService(serviceId);
          } catch (error) {
            console.error('Erreur lors de l\'ouverture du modal de service:', error);
          }
        };

        detailBtn.addEventListener('click', handleClick);

        this.cleanup.push(() => {
          detailBtn.removeEventListener('click', handleClick);
        });
      }
    });
  }

  private setupScrollAnimations(): void {
    const handleScroll = () => {
      const serviceCards = this.element.querySelectorAll('.service-card');
      serviceCards.forEach((card, index) => {
        const rect = card.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight * 0.9;

        if (isVisible) {
          setTimeout(() => {
            (card as HTMLElement).style.opacity = '1';
            (card as HTMLElement).style.transform = 'translateY(0)';
          }, index * animationConfig.cardAnimationDelay);
        }
      });
    };

    const serviceCards = this.element.querySelectorAll('.service-card');
    serviceCards.forEach(card => {
      (card as HTMLElement).style.opacity = '0';
      (card as HTMLElement).style.transform = 'translateY(20px)';
      (card as HTMLElement).style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out';
    });

    window.addEventListener('scroll', handleScroll);
    setTimeout(handleScroll, 100);

    this.cleanup.push(() => {
      window.removeEventListener('scroll', handleScroll);
    });
  }

  private renderBackgroundEffect(): string {
    return `
      <div class="absolute inset-0 overflow-hidden">
        <!-- Grille de fond modernisée avec motif subtil -->
        <div class="absolute inset-0 opacity-20" style="background-image: radial-gradient(rgba(237, 76, 103, 0.1) 1px, transparent 1px), radial-gradient(rgba(52, 152, 219, 0.1) 1px, transparent 1px); background-size: 30px 30px, 25px 25px; background-position: 0 0, 15px 15px;"></div>

        <!-- Dégradé de fond plus intense -->
        <div class="absolute inset-0 bg-gradient-to-b from-[#0b1220] via-[#0b1220]/95 to-[#0b1220]/90"></div>

        <!-- Vignette centrale plus profonde pour mettre en valeur les cards -->
        <div class="absolute inset-0 pointer-events-none" style="background: radial-gradient(ellipse at center, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.10) 40%, rgba(0,0,0,0.42) 100%);"></div>

        <!-- Assombrissement haut/bas pour plus de profondeur -->
        <div class="absolute inset-x-0 top-0 h-64 pointer-events-none" style="background: linear-gradient(to bottom, rgba(0,0,0,0.38), rgba(0,0,0,0.0));"></div>
        <div class="absolute inset-x-0 bottom-0 h-64 pointer-events-none" style="background: linear-gradient(to top, rgba(0,0,0,0.38), rgba(0,0,0,0.0));"></div>

        <!-- Formes organiques vivantes - plus grandes et plus colorées -->
        <div class="absolute -top-[15%] -right-[5%] w-[70%] h-[70%] bg-gradient-to-br from-[#ed4c67]/25 to-[#3498db]/10 rounded-full blur-3xl opacity-60 wave-anim" style="animation-delay: 0.5s;"></div>
        <div class="absolute -bottom-[20%] -left-[10%] w-[80%] h-[80%] bg-gradient-to-tr from-[#3498db]/25 to-[#ed4c67]/10 rounded-full blur-3xl opacity-60 wave-anim-alt"></div>

        <!-- Cercles radiatifs pulsants -->
        <div class="radiate absolute top-1/4 left-1/4 w-40 h-40 bg-[#ed4c67]/10 rounded-full blur-xl"></div>
        <div class="radiate absolute bottom-1/3 right-1/4 w-60 h-60 bg-[#3498db]/10 rounded-full blur-xl" style="animation-delay: 1s;"></div>
        <div class="radiate absolute top-2/3 left-1/3 w-32 h-32 bg-[#e67e22]/10 rounded-full blur-xl" style="animation-delay: 2s;"></div>
      </div>
    `;
  }

  private renderTitleSection(): string {
    return `
      <div class="text-center mb-20" style="border-left: none !important; padding-left: 0 !important;">


        <!-- Titre plus imposant -->
        <h2 class="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight fade-in-up delay-200 text-center" style="border-left: none !important; padding-left: 0 !important;">
          <span class="text-gradient-animate" style="filter: brightness(1.2) drop-shadow(0 0 12px rgba(237, 76, 103, 0.4));">${textContent.sectionTitle}</span>
        </h2>

        <!-- Description plus claire -->
        <p class="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
          ${textContent.sectionSubtitle}
        </p>

        <!-- Élément décoratif -->
        <div class="w-24 h-1 bg-gradient-to-r from-[#ed4c67] to-[#3498db] mx-auto mt-8 rounded-full"></div>
      </div>
    `;
  }

  private renderServiceCardSection(services: ServiceDetailExtended[]): string {
    const cardsHtml = services.map(service => this.renderServiceCard(service)).join('');

    return `
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        ${cardsHtml}
      </div>
    `;
  }

  private renderServiceCard(service: ServiceDetailExtended): string {
    const { id, title, description, features, featureColor, icon } = service;

    return `
      <div class="service-card group" data-service-id="${id}">
        <div class="service-card-inner bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 h-full flex flex-col transition-all duration-500 relative overflow-hidden group-hover:shadow-xl group-hover:translate-y-[-5px]" style="--feature-color: ${featureColor};">

          <!-- Effet de brillance au survol -->
          <div class="absolute inset-0 bg-gradient-to-br from-white/15 via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>

          <!-- Ligne de couleur en haut -->
          <div class="absolute top-0 left-0 w-full h-1.5 opacity-80" style="background: linear-gradient(to right, ${featureColor}, transparent 90%);"></div>

          <!-- En-tête icône + titre alignés -->
          <div class="mb-5 flex items-center gap-4 relative">
            <div class="relative flex items-center justify-center">
              <!-- Micro‑halo coloré au hover (couleur du service) -->
              <div class="absolute opacity-0 group-hover:opacity-80 transition-opacity duration-500 rounded-full"
                   style="left:-8px; top:-8px; right:-8px; bottom:-8px; background:${featureColor}33; filter: blur(14px);"></div>
              <!-- Icône avec bordure totalement ronde -->
              <div class="w-14 h-14 rounded-full flex items-center justify-center bg-gradient-to-br from-white/20 to-white/5 border border-white/30 group-hover:scale-110 transition-transform duration-500 shadow-inner">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" shape-rendering="geometricPrecision">
                  ${icon.trim().startsWith('<')
                    ? icon
                    : `<path stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"2\" vector-effect=\"non-scaling-stroke\" d=\"${icon}\" />`}
                </svg>
              </div>
            </div>
            <h3 class="text-2xl font-bold text-white/95 transition-colors duration-300 m-0">${title}</h3>
          </div>

          <!-- Description -->
          <p class="text-white/80 text-lg mb-5 flex-grow">${description}</p>

          <!-- Caractéristiques -->
          <div class="mb-6">
            <div class="flex flex-col space-y-3">
              ${features.slice(0, 3).map(feature => `
                <div class="flex items-start space-x-3 p-2.5 rounded-lg transition-all duration-300 backdrop-blur-sm group-hover:bg-white/10 border border-transparent group-hover:border-white/20">
                  <span class="w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 bg-white/60" style="background-color: white; opacity: 0.6;"></span>
                  <span class="text-white/85 group-hover:text-white/95 text-base transition-colors duration-300">${feature}</span>
                </div>
              `).join('')}
              ${features.length > 3 ? `
                <div class="text-sm mt-1 ml-5 font-medium animate-pulse-slow" style="color: ${featureColor};">
                  + ${features.length - 3} autres caractéristiques
                </div>
              ` : ''}
            </div>
          </div>

          <!-- Bouton pour en savoir plus -->
          <div class="mt-auto">
            <button type="button" class="service-details-btn w-full py-3 px-5 rounded-full bg-gradient-to-r from-white/10 to-white/5 border border-white/20 text-white text-sm font-medium flex items-center justify-center space-x-2 transition-all duration-500" style="--feature-color: ${featureColor};">
              <span>Découvrir en détail</span>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderCTA(): string {
    return `
      <div class="services-cta-wrapper relative mt-20 overflow-hidden rounded-full p-0.5 border border-white/20 text-white group hover:shadow-lg hover:shadow-[#ed4c67]/5 transition-all duration-700">
        <!-- Bordure animée -->
        <div class="services-cta-border absolute inset-0 bg-gradient-to-r from-[#ed4c67] via-[#3498db] to-[#ed4c67] opacity-30 bg-[length:200%_100%] animate-gradient-flow"></div>
        <div class="services-cta-inner relative bg-gradient-to-br from-[#34495e]/90 to-[#131313]/90 backdrop-blur-xl rounded-full p-8 md:p-10">
          <div class="services-cta-layout flex flex-col lg:flex-row items-center justify-between gap-8 relative z-10">
            <div class="services-cta-text lg:w-2/3">
              <h3 class="services-cta-title text-3xl lg:text-4xl font-bold mb-4 text-gradient">
                ${textContent.ctaTitle}
              </h3>
              <p class="services-cta-description text-lg text-white/80 max-w-2xl">
                ${textContent.ctaDescription}
              </p>
            </div>
            <div class="services-cta-actions flex flex-wrap gap-5">
              <a href="contact.html" class="services-cta-button group relative overflow-hidden rounded-full py-4 px-8 inline-flex items-center justify-center shine-effect">
                <span class="absolute inset-0 bg-gradient-to-r from-[#ed4c67] to-[#e84393] rounded-full"></span>
                <span class="relative z-10 mr-2 flex items-center">
                  ${textContent.ctaButtonText}
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderServiceDetailModal(): string {
    return `
      <div id="service-detail-modal" class="fixed inset-0 z-50 flex items-center justify-center opacity-0 pointer-events-none transition-all duration-500" role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <!-- Fond avec effet flou -->
        <div class="modal-backdrop absolute inset-0 bg-[#131313]/75 backdrop-blur-xl opacity-0 transition-opacity duration-500"></div>

        <!-- Contenu du modal -->
        <div class="modal-content relative max-w-5xl w-full max-h-[88vh] md:max-h-[90vh] bg-gradient-to-br from-[#34495e]/95 to-[#2c3e50]/95 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl transform translate-y-8 scale-95 opacity-0 transition-all duration-500 p-6 md:p-8 mx-4 flex flex-col">
          <!-- Liseret de couleur dynamique en haut (sera ajusté dynamiquement par JS) -->
          <div id="modal-color-bar" class="absolute top-0 left-0 w-full h-2 opacity-80"></div>

          <!-- Bouton de fermeture -->
          <button id="modal-close-btn" class="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#ed4c67]/30 text-white/80 hover:text-white transition-all duration-300 z-20 border border-white/10 hover:border-white/30 focus:outline-none focus:ring-2 focus:ring-white/30" style="transition: all 0.3s ease;" aria-label="Fermer">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 1L1 17M1 1L17 17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>

          <!-- Contenu principal avec zone de défilement -->
          <div class="modal-body flex-1 min-h-0 overflow-y-auto py-4 px-2 service-content-scroll">
            <div id="modal-content-container" class="relative z-10"></div>
          </div>

          <!-- Footer avec navigation et position -->
          <div id="modal-footer" class="modal-footer bg-gradient-to-r from-[#34495e]/95 to-[#2c3e50]/95 flex items-center justify-between gap-4 border-t border-white/10 p-3 md:p-4" style="box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);">
            <button id="prev-service-btn" class="nav-btn w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-all duration-300 border border-white/15 hover:border-white/30 focus:outline-none focus:ring-2 focus:ring-white/30" style="transition: all 0.3s ease;">
              <svg width="22" height="18" viewBox="0 0 24 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 1L1 8L8 15M1 8H23" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            <div id="service-position" class="flex-1 text-center px-4 py-2 rounded-full bg-white/10 text-white/80 text-sm font-semibold border border-white/15" style="animation: pulse 2s infinite;">
              <span id="current-service-index">1</span>/<span id="total-services">${services.length}</span>
            </div>

            <button id="next-service-btn" class="nav-btn w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-all duration-300 border border-white/15 hover:border-white/30 focus:outline-none focus:ring-2 focus:ring-white/30" style="transition: all 0.3s ease;">
              <svg width="22" height="18" viewBox="0 0 24 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 1L23 8L16 15M23 8H1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <style>
        @keyframes pulse {
          0%, 100% { background-color: rgba(255, 255, 255, 0.1); }
          50% { background-color: rgba(255, 255, 255, 0.2); }
        }

        #prev-service-btn:hover, #next-service-btn:hover {
          transform: scale(1.1);
        }

        #modal-close-btn:hover {
          transform: rotate(90deg);
        }
      </style>
    `;
  }

  public getElement(): HTMLElement {
    return this.element;
  }

  public destroy(): void {
    this.cleanup.forEach(fn => fn());
    this.cleanup = [];
  }
}
